import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { QuickAddItemForm } from '@/components/QuickAddItemForm';
import { T, RADIUS, SHADOW, CATEGORY_EMOJI } from '@/lib/tokens';
import logoOnWhite from '@/assets/logo-on-white.svg';
import posthog from '@/lib/posthog';

interface AddedItem {
  id: string;
  name: string;
  category: string;
}

export default function OnboardingAddItems() {
  const navigate = useNavigate();
  const location = useLocation();
  const groupId = (location.state as any)?.groupId as string | undefined;
  const groupName = (location.state as any)?.groupName as string | undefined;

  const [addedItems, setAddedItems] = useState<AddedItem[]>([]);

  // Redirect if no group context
  useEffect(() => {
    if (!groupId) {
      navigate('/onboarding/create-group', { replace: true });
    }
  }, [groupId, navigate]);

  const handleItemAdded = (item: AddedItem) => {
    setAddedItems((prev) => [item, ...prev]);
  };

  const handleContinue = () => {
    posthog.capture('onboarding_items_added', { count: addedItems.length });
    navigate('/onboarding/invite', {
      state: { groupId, groupName, addedItemCount: addedItems.length },
    });
  };

  if (!groupId) return null;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.bg,
        display: 'flex',
        justifyContent: 'center',
        padding: '40px 20px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img
            src={logoOnWhite}
            alt="Share Shack"
            style={{ width: 36, height: 36, display: 'block', margin: '0 auto' }}
          />
          <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginTop: 4 }}>
            Share Shack
          </div>
        </div>

        {/* Step indicator dots — step 2 of 3 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            marginBottom: 24,
          }}
        >
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              style={{
                height: 8,
                width: s === 2 ? 32 : 8,
                borderRadius: 4,
                background: s === 2 ? T.accent : s < 2 ? T.accentBorder : T.surfaceSubtle,
                transition: 'all 0.2s',
              }}
            />
          ))}
        </div>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, color: T.text, margin: '0 0 8px' }}>
            Add a few items to share
          </h1>
          <p style={{ fontSize: 14, color: T.textSec, lineHeight: 1.65, margin: 0 }}>
            Your group will see these when they join. You can always add more later.
          </p>
        </div>

        {/* Quick add form — simplified for onboarding */}
        <QuickAddItemForm
          groupId={groupId}
          resetOnSubmit
          onItemCreated={handleItemAdded}
          submitLabel="Add Item"
          isOnboarding
        />

        {/* Added items list */}
        {addedItems.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: T.textSec, marginBottom: 8 }}>
              {addedItems.length} {addedItems.length === 1 ? 'item' : 'items'} added
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {addedItems.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    border: `1px solid ${T.border}`,
                    borderRadius: RADIUS.sm,
                    background: T.surface,
                  }}
                >
                  <span style={{ fontSize: 16 }}>{CATEGORY_EMOJI[item.category] || '📦'}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Continue button — always visible */}
        <button
          onClick={handleContinue}
          style={{
            width: '100%',
            padding: '13px',
            borderRadius: RADIUS.md,
            background: T.accent,
            color: T.white,
            border: 'none',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            boxShadow: SHADOW.btn.accent,
            marginTop: 24,
          }}
        >
          {addedItems.length > 0 ? 'Continue — invite your group' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
